/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { ContentBlock, useBreakpoint, Heading, ErrorBoundary } from 'sarsaparilla';
import { useUserLocation } from 'shared-ui/geolocation';
import { useSearchResultsQuery } from './api/search.queries';
import { FiltersProvider, useFiltersState } from './FiltersContext';
import { ResultsList } from './ResultsList/ResultsList';
import { HomepageMap } from './HomepageMap/HomepageMap';

import styles from './ExploreDestinationsMap.module.scss';

function ExploreDestinationsMapContent() {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const userLocation = useUserLocation();
    const [filters, setFilters] = useFiltersState();
    const { data, isFetching } = useSearchResultsQuery();
    const prevLocationRef = React.useRef(userLocation);

    // Initialize filters from user location only when no explicit search location exists.
    React.useEffect(() => {
        const hasExistingLatLng = filters.lat && filters.lng;
        const hasBoundingBox =
            filters.latSw && filters.lngSw && filters.latNe && filters.lngNe;

        if (
            !filters.location &&
            !hasBoundingBox &&
            !hasExistingLatLng &&
            userLocation !== null
        ) {
            setFilters({
                lat: userLocation.latitude,
                lng: userLocation.longitude,
                location: userLocation.location ?? null,
            });
        }
    }, [filters, setFilters, userLocation]);

    // When navigator upgrades a less accurate location (IP or the default
    // fallback), update filters if they still hold the previous coordinates so
    // the map moves to the more accurate position.
    React.useEffect(() => {
        const prev = prevLocationRef.current;
        prevLocationRef.current = userLocation;

        if (
            prev !== null &&
            prev.source !== 'navigator' &&
            userLocation?.source === 'navigator' &&
            filters.lat === prev.latitude &&
            filters.lng === prev.longitude
        ) {
            setFilters({
                lat: userLocation.latitude,
                lng: userLocation.longitude,
                location: null,
            });
        }
    }, [userLocation, filters.lat, filters.lng, setFilters]);

    return (
        <div className={styles.container}>
            <div
                className={cx(styles.list, {
                    [styles.isExpanded]: isExpanded,
                })}
            >
                {/* ResultsList renders its own loading placeholders, and keeping
                    it mounted means the search control stays usable while the
                    first results load. */}
                <ResultsList />
            </div>
            <div className={styles.map}>
                <HomepageMap
                    data={data}
                    isFetching={isFetching}
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                />
            </div>
        </div>
    );
}

export function ExploreDestinationsMap() {
    const { isMdAndDown } = useBreakpoint();

    return (
        <ErrorBoundary>
            <FiltersProvider>
                {/* The structure here is intentional so that the FiltersProvider can retain the map state if the user shrinks and re-expands the browser window */}
                {isMdAndDown ? null : (
                    <ContentBlock className="my-6">
                        <Heading
                            headingLevel={2}
                            appearance="h4"
                            hasUnderline="left"
                            className="mb-1"
                        >
                            Explore Destinations & Activities
                        </Heading>

                        <ExploreDestinationsMapContent />
                    </ContentBlock>
                )}
            </FiltersProvider>
        </ErrorBoundary>
    );
}

export default ExploreDestinationsMap;
